import { motion } from "framer-motion";
import { Accessibility, CircleOff, Users } from "lucide-react";
import { getFloorBadgeLabel } from "@/lib/utils";
import RoomSwatch from "@/components/tpr/RoomSwatch/RoomSwatch";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import type { Room } from "@/types/Room";
import { getRoomCapacity } from "@/utils/roomCapacity";

interface RoomsGridProps {
  rooms: Room[];
  locationSlug: string;
}

const RoomsGrid = ({ rooms, locationSlug }: RoomsGridProps) => {
  const animation = {
    initial: { opacity: 0, scale: 0.9, skewX: 5 },
    animate: { opacity: 1, scale: 1, skewX: 0 },
  };

  if (!rooms || rooms.length === 0) {
    return (
      <div className="bg-gray-100 border-2 border-gray-100 rounded-lg p-6 border-solid dark:bg-slate-900 dark:border-gray-900">
        <p className="text-center text-gray-500">No rooms available at this location.</p>
      </div>
    );
  }

  return (
    <div className="bg-gray-100 border-2 border-gray-100 rounded-lg p-6 border-solid dark:bg-slate-900 dark:border-gray-900">
      <div className="grid grid-cols-2 md:grid-cols-3 2xl:grid-cols-4 gap-4">
        {rooms.map((room) => (
          <div key={room.id} className="relative">
            <a href={`/location/${locationSlug}/room/${room.id}`}>
              <motion.div className="rounded-md" initial={animation.initial} animate={animation.animate}>
                <div className="aspect-square overflow-hidden rounded-t-lg bg-gray-200 relative">
                  {room.gallery && room.gallery.length > 0 ? (
                    <img
                      className="w-full h-full object-cover transform-gpu scale-100 hover:scale-110 transition-transform duration-300"
                      src={room.gallery[0]}
                      alt={room.name}
                    />
                  ) : (
                    <div className={`w-full h-full ${room.swatch}`} />
                  )}

                  {/* Floor badge - top left */}
                  {room.floor && (
                    <TooltipProvider delayDuration={0}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div className="absolute top-2 left-2 bg-white/90 dark:bg-slate-800/90 rounded px-2 py-1 text-xs font-semibold shadow-sm cursor-help">
                            {getFloorBadgeLabel(room.floor)}
                          </div>
                        </TooltipTrigger>
                        <TooltipContent side="bottom">{room.floor}</TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  )}

                  {/* Accessibility badge - top right */}
                  {room.isAccessible !== undefined && (
                    <TooltipProvider delayDuration={0}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div className="absolute top-2 right-2 bg-white/90 dark:bg-slate-800/90 rounded p-1.5 shadow-sm cursor-help">
                            {room.isAccessible ? (
                              <Accessibility className="w-4 h-4 text-green-600" />
                            ) : (
                              <CircleOff className="w-4 h-4 text-gray-400" />
                            )}
                          </div>
                        </TooltipTrigger>
                        <TooltipContent side="bottom">
                          {room.isAccessible ? "Wheelchair accessible" : "Not wheelchair accessible"}
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  )}

                  {/* Capacity badge - bottom right */}
                  {(() => {
                    const capacity = getRoomCapacity(room);
                    return (
                      <TooltipProvider delayDuration={0}>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <div className="absolute bottom-2 right-2 bg-white/90 dark:bg-slate-800/90 rounded px-2 py-1 shadow-sm cursor-help flex items-center gap-1">
                              <Users className="w-3.5 h-3.5 text-gray-700 dark:text-gray-300" />
                              <span className="text-xs font-semibold">{capacity}</span>
                            </div>
                          </TooltipTrigger>
                          <TooltipContent side="top">
                            Capacity: {capacity} {capacity === 1 ? "person" : "people"}
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    );
                  })()}
                </div>
              </motion.div>

              <div className="bg-gray-200 dark:bg-slate-800 rounded-b-lg p-2 text-sm md:text-base flex items-center gap-2 min-h-[3rem]">
                <RoomSwatch swatch={room.swatch} name={room.name} hideLabel />
                <span className="truncate" aria-label={`Room ${room.name}`}>
                  {room.name}
                </span>
              </div>
            </a>
          </div>
        ))}
      </div>
    </div>
  );
};

export default RoomsGrid;
